import React from 'react';
import styled, { css } from 'styled-components';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { slideUp } from 'styles/animations';
import { UnstyledButton } from 'components';
import { withAlerts, AlertsState } from 'context/alerts';

interface AlertType {
  [key: string]: any;
}

function getAlertStyles(type: string, colors: any) {
  const types: AlertType = {
    danger: css`
      color: ${colors.danger};
      background-color: ${colors.dangerBg};
      border-color: ${colors.dangerBorder};
    `,
    success: css`
      color: ${colors.success};
      background-color: ${colors.successBg};
      border-color: ${colors.successBorder};
    `,
    warning: css`
      color: ${colors.warning};
      background-color: ${colors.warningBg};
      border-color: ${colors.warningBorder};
    `,
  };
  return types[type];
}

const StyledAlertBox = styled.div<{ alertType?: string; floating?: boolean }>`
  ${slideUp}
  position: relative;
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.md}
    ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  border-radius: 8px;
  margin: 1em auto;
  border: 1px solid transparent;
  min-height: 70px;
  max-width: 600px;
  display: flex;
  align-items: center;
  box-shadow: ${props =>
    props.floating ? '0px 2px 10px 0 rgba(0, 0, 0, 0.2)' : 'none'};

  /* info-box */
  color: ${p => p.theme.colors.info};
  background-color: ${p => p.theme.colors.infoBg};
  border-color: ${p => p.theme.colors.infoBorder};
  z-index: 1000;

  ${p => p.alertType && getAlertStyles(p.alertType, p.theme.colors)}
  ${props =>
    props.floating &&
    `
    position: absolute;
    left: 0;
    right: 0;
  `}
`;
const StyledAlertIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: ${p => p.theme.spacing.md};
`;
const StyledAlertContent = styled.div`
  margin-right: ${p => p.theme.spacing.md};
  flex: 1;
`;
const StyledAlertDismiss = styled(UnstyledButton)`
  font: inherit;
  align-self: flex-start;
  margin-left: auto;
  padding: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSize['2xl']};
  font-weight: 700;
  line-height: 1;
  color: ${p => p.theme.colors.actionFg};
  opacity: 0.5;

  &:hover {
    color: ${p => p.theme.colors.actionFg};
    text-decoration: none;
    opacity: 0.75;
  }

  > span {
    cursor: pointer;
  }
`;

interface Props {
  alertType: string;
  floating?: boolean;
  alerts: AlertsState;
  children?: React.ReactNode;
}

function getAlertIcon(type: string) {
  switch (type) {
    case 'success':
      return <CheckCircle size={20} aria-hidden={true} />;
    case 'warning':
      return <AlertTriangle size={20} aria-hidden={true} />;
    case 'danger':
      return <XCircle size={20} aria-hidden={true} />;
    default:
      return <Info size={20} aria-hidden={true} />;
  }
}

const AlertBoxWrapper = (props: Props) => {
  const dismiss = () => {
    props.alerts.clearMessage();
  };

  if (!props.alerts.message) return null;
  return (
    <StyledAlertBox {...props}>
      <StyledAlertIcon>{getAlertIcon(props.alertType)}</StyledAlertIcon>
      <StyledAlertContent>{props.children}</StyledAlertContent>
      {props.alerts.dismissable && (
        <StyledAlertDismiss
          onClick={() => dismiss()}
          type="button"
          aria-label="Close"
        >
          <span aria-hidden="true">×</span>
        </StyledAlertDismiss>
      )}
    </StyledAlertBox>
  );
};

const AlertBox = withAlerts(AlertBoxWrapper);

export { AlertBox };
